import { memo, useContext, useEffect, useMemo, useState } from 'react'

import { fetchMetasearchDiscount } from 'actions/MetasearchDiscountActions'
import { trackEvent } from 'analytics/analytics'
import {
  offerPageDurationChangeEvent,
  offerPageGuestSelectionEvent,
  offerPageHotLeadEvent,
  offerPageSoldOutEvent,
} from 'analytics/snowplow/events'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import usePrefetchFlightsFromOffer from 'components/OfferPage/hooks/prefetchFlights/usePrefetchFlightsFromOffer'
import { getNextFilterWithCapacityCheckInMiddleware } from 'components/OfferPage/utils'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import type { OfferPageDispatchAction, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import useMetasearchRuleId from 'hooks/useMetasearchRuleId'
import useMountedEffect from 'hooks/useMountedEffect'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import {
  getExperimentEnabledDecision,
  getOptimizelyExperimentVariation,
  isAllowedToTriggerExperiment,
} from 'lib/optimizely/optimizelyUtils'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { getInitialPropertyPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import {
  arePropertyRatesErrored,
  arePropertyRatesLoading,
  getPropertyCheapestHotelOnlyRateForDiscount,
  getPropertyCombinedRatesByRoom,
  getPropertyOfferMapping,
} from 'selectors/offerPage/offerPageSelectors'
import { isSoldOutForDatesAndOccupancies } from 'selectors/offerPage/propertyOfferPageSelectors'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { ratesStatusFact } from 'tripPlanner/assistant/surfaces/offerPage/buildOfferPageSnapshot'
import OfferPageAssistantHost from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantHost'
import { OfferPageAssistantSourceRegistration } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import {
  createOfferPageSnapshotSelection,
  type LmePropertyOfferPageSnapshotSource,
  type SppPropertyOfferPageSnapshotSource,
} from 'tripPlanner/assistant/surfaces/offerPage/offerPageSnapshotTypes'
import useOfferPageRoomPackageLoyaltyEarnTotals from 'tripPlanner/assistant/surfaces/offerPage/useOfferPageRoomPackageLoyaltyEarnTotals'
import OfferPageAssistantExpandedContext from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantExpandedContext'
import type { PropertyRoomRateViewForAssistant } from 'tripPlanner/assistant/surfaces/offerPage/offerPageContextTypes'
import { useSyncBedbankTripItem } from 'tripPlanner/hooks/bookmarks/useSyncBedbankTripItem'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'

import PropertyContent from './PropertyContent'
import propertyMobileFilterMiddleware from './PropertyMobileFilterMiddleware'
import PropertySppStructuredData from './PropertySppStructuredData'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [propertyMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]
const defaultRooms = [generateOfferPageRoom()]
const EMPTY_HOTEL_PACKAGE_VIEWS: Array<App.OfferPackageOptionView> = []
const EMPTY_BEDBANK_PACKAGE_VIEWS: Array<App.BedbankOfferPackageView> = []
const EMPTY_PROPERTY_ROOM_RATE_VIEWS: Array<PropertyRoomRateViewForAssistant> = []

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer | App.BedbankOffer
}

interface PropertyOfferAssistantSourceRegistrationProps {
  property: App.Property.Property
  variant: 'lme_property' | 'spp_property'
  offerPageState: OfferPageState
  lpcOffer?: App.HotelOffer
  /**
   * Pre-resolved by `PropertyContainer` from the original, undivided `offer` prop (before the
   * `lpcOffer`/`bedbankOffer` split below) -- `OfferBase.luxPlus` exists on both `App.HotelOffer`
   * and `App.BedbankOffer`, so this covers both SPP sourcing paths with a single field instead of
   * threading a second, rarely-populated `bedbankOffer` object through just for this.
   */
  luxPlusAccess?: App.OfferLuxPlusAccessType
}

function PropertyOfferAssistantSourceRegistration({
  property,
  variant,
  offerPageState,
  lpcOffer,
  luxPlusAccess,
}: PropertyOfferAssistantSourceRegistrationProps) {
  const currencyCode = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  // Matches `PropertyRateOption`'s own gating exactly, so the snapshot only strips "valid for N
  // guests" inclusion text the real tile would actually strip for a subscribed customer.
  const isInclusionsValidityEnabled = useAppSelector(appState => !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity])
  const roomRateViews = useAppSelector(appState => getPropertyCombinedRatesByRoom(offerPageState, appState))
  const ratesLoading = useAppSelector(appState => arePropertyRatesLoading(offerPageState, appState))
  const ratesErrored = useAppSelector(appState => arePropertyRatesErrored(offerPageState, appState))
  const ratesStatus = ratesStatusFact(ratesLoading, ratesErrored)
  const propertyIsUltraLux = useAppSelector(appState => getPropertyOfferMapping(offerPageState, appState)?.isUltraLux ?? false)
  // Read-only, matching `isTreatmentSppBedbankPromosVariation` above in PropertyContainer -- not
  // `useExperiment`, which also fires a Snowplow activation event gated by `canActivate`/viewport
  // visibility that doesn't apply to this non-viewport-gated source registration. Still applies the
  // same UTM exclusion gate `useExperiment` applies internally (`isAllowedToTriggerExperiment`) --
  // without it, GHA-redirected metasearch traffic the real `PropertyRoomOption`'s own
  // `useExperiment(Experiments.croUrgencyOnSppRooms, ...)` call excludes would still read as
  // enabled here, letting the snapshot claim an urgency label the real page never renders.
  const isSppRoomUrgencyEnabled = useAppSelector(appState => isAllowedToTriggerExperiment(true, appState.utm.content, Experiments.croUrgencyOnSppRooms.id) && getExperimentEnabledDecision(
    getOptimizelyExperimentVariation(appState, Experiments.croUrgencyOnSppRooms.id),
  ))
  // Loyalty-earn totals require a real API call (`useLoyaltyPointsEarnCalculator`) -- only compute
  // them once the assistant is actually expanded, rather than firing on every page load.
  const isAssistantExpanded = useContext(OfferPageAssistantExpandedContext)
  const { totalsByGroupKey: roomPackageLoyaltyEarnTotals, status: loyaltyStatus } = useOfferPageRoomPackageLoyaltyEarnTotals({
    offerPageState,
    currentRegionCode,
    hotelPackageViews: EMPTY_HOTEL_PACKAGE_VIEWS,
    bedbankPackageViews: EMPTY_BEDBANK_PACKAGE_VIEWS,
    propertyRoomRateViews: isAssistantExpanded ? roomRateViews : EMPTY_PROPERTY_ROOM_RATE_VIEWS,
    propertyIsUltraLux,
  })
  const source = useMemo<
    LmePropertyOfferPageSnapshotSource | SppPropertyOfferPageSnapshotSource
  >(() => ({
    key: `property:${property.id}`,
    variant,
    selection: createOfferPageSnapshotSelection(offerPageState),
    priceFormatContext: {
      currencyCode,
      regionCode: currentRegionCode,
    },
    property,
    roomRateViews,
    ratesStatus,
    roomPackageLoyaltyEarnTotals,
    loyaltyStatus,
    isInclusionsValidityEnabled,
    isSppRoomUrgencyEnabled,
    lpcOffer,
    isLuxPlusEnabled,
    luxPlusAccess,
  }), [currencyCode, currentRegionCode, offerPageState, property, ratesStatus, roomPackageLoyaltyEarnTotals, loyaltyStatus, roomRateViews, variant, isInclusionsValidityEnabled, isSppRoomUrgencyEnabled, lpcOffer, isLuxPlusEnabled, luxPlusAccess])

  return <OfferPageAssistantSourceRegistration source={source} />
}

function PropertyContainer(props: Props) {
  const { property, offer } = props
  const [lpcOffer, bedbankOffer] = isBedbankOffer(offer) ? [undefined, offer] : [offer, undefined]
  const propertyOfferVariant = isBedbankOffer(offer) ? 'spp_property' : 'lme_property'
  // Resolved from the original, undivided `offer` -- `OfferBase.luxPlus` exists on both
  // `App.HotelOffer` and `App.BedbankOffer`, so this covers both SPP sourcing paths uniformly
  // rather than needing separate lpcOffer/bedbankOffer handling downstream.
  const luxPlusAccess = offer.luxPlus.access
  const [middlewares, setMiddlewares] = useState(defaultMiddlewares)
  const dispatch = useAppDispatch()
  const defaults = useAppSelector(state => getInitialPropertyPageState(state, lpcOffer, bedbankOffer, property))

  const isMobileDevice = useIsMobileDevice()
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? 1,
    offer: lpcOffer,
    bedbankOffer,
  }, middlewares)
  const cheapestHotelOnlyRate = useAppSelector(appState => getPropertyCheapestHotelOnlyRateForDiscount(state, appState))
  const discountToken = useMetasearchPriceId()
  const rateRuleId = useMetasearchRuleId()
  const currentCurrency = useAppSelector(appState => appState.geo.currentCurrency)

  useSyncHotelTripItem(state, lpcOffer)
  useSyncBedbankTripItem(state, bedbankOffer)
  usePrefetchFlightsFromOffer({
    offer,
    offerPageState: state,
    flightsEnabled: property.areFlightsEnabled,
    forceBundled: property.mustBundleWithFlights,
  })
  const { isAnyVariant: isNewMobileBookingFlowEnabled } = useExperiment(
    Experiments.croAlwaysOnNewMobileBooking,
    { canActivate: isMobileDevice },
  )

  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  useEffect(() => {
    if (useNewMobileBookingFlow) {
      setMiddlewares(mobileVariantMiddlewares)
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [useNewMobileBookingFlow])

  useEffect(() => {
    stateDispatch({ type: OfferPageActions.setProperty, property })
  }, [stateDispatch, property])

  useMountedEffect(() => {
    // the first loaded offer from an SSR is a summary. We then fetch the full version
    // This ensures the offer page state has our latest version
    if (lpcOffer) stateDispatch({ type: OfferPageActions.setOffer, offer: lpcOffer })
    if (bedbankOffer) stateDispatch({ type: OfferPageActions.setBedBankOffer, bedbankOffer })
  }, [lpcOffer, bedbankOffer])

  useMountedEffect(() => {
    trackEvent(offerPageDurationChangeEvent(offer, state))
  }, [state.duration])

  useMountedEffect(() => {
    if (state.rooms) {
      trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  const isSoldOut = useAppSelector(appState => isSoldOutForDatesAndOccupancies(state, appState))
  useEffect(() => {
    if (isSoldOut) {
      trackEvent(offerPageSoldOutEvent(offer, state))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [state.checkInDate, state.checkOutDate, state.rooms, isSoldOut])

  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration: state.duration,
    occupancy: state.rooms,
    defaultOccupancy: defaultRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useEffect(() => {
    if (
      discountToken &&
      cheapestHotelOnlyRate &&
      'service' in cheapestHotelOnlyRate &&
      cheapestHotelOnlyRate.service.type === 'bedbank' &&
      state.checkInDate &&
      state.checkOutDate
    ) {
      dispatch(fetchMetasearchDiscount({
        token: discountToken,
        propertyId: cheapestHotelOnlyRate.service.propertyId,
        checkIn: state.checkInDate,
        checkOut: state.checkOutDate,
        price: cheapestHotelOnlyRate.totals.displayPrice,
        propertyFees: cheapestHotelOnlyRate.totals.propertyFees,
        occupancy: serialiseOccupancy(state.rooms),
        currency: currentCurrency,
        mx: cheapestHotelOnlyRate.totals.mx,
        rateRuleId,
      }))
    }
  }, [cheapestHotelOnlyRate, currentCurrency, discountToken, dispatch, rateRuleId, state.checkInDate, state.checkOutDate, state.rooms])

  /* -------- START of treatment for SPP for bedbank properties with promotions A/B test -------- */

  const hasBedbankPromotions = !!bedbankOffer?.promotions.length && !!property.promotion?.endIsoDateTime
  const isTreatmentSppBedbankPromosVariation = useAppSelector(state => getExperimentEnabledDecision(
    getOptimizelyExperimentVariation(state, Experiments.sppBedbankPromos.id),
  ))
  useExperiment(Experiments.sppBedbankPromos, {
    canActivate: hasBedbankPromotions && isTreatmentSppBedbankPromosVariation,
  })

  /* --------- END of treatment for SPP for bedbank properties with promotions A/B test --------- */

  // Ensure descendants can depend on `offerPageState.property` being set.
  if (!state.property) return <LoadingIndicator />

  return <OfferPageStateProvider state={state} dispatch={stateDispatch}>
    <PropertySppStructuredData offer={offer} />
    <OfferPageAssistantHost offerPageState={state}>
      <PropertyOfferAssistantSourceRegistration property={property} variant={propertyOfferVariant} offerPageState={state} lpcOffer={lpcOffer} luxPlusAccess={luxPlusAccess} />
      <PropertyContent
        property={property}
        offer={offer as App.HotelOffer /* TODO(HP-1495): Remove dependency. */}
        socialProofRecentPurchases={offer.stats?.recentPurchases}
        socialProofImage={offer.image}
      />
    </OfferPageAssistantHost>
  </OfferPageStateProvider>
}

export default memo(PropertyContainer)
